<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";

import AnalyticsConsentBanner from "@/components/AnalyticsConsentBanner.vue";
import AppFooter from "@/components/AppFooter.vue";
import AppHeader from "@/components/AppHeader.vue";
import AppShell from "@/components/AppShell.vue";
import { useAuthStore } from "@/stores/auth";

const auth = useAuthStore();
const route = useRoute();

/**
 * Guest routes render with the top-nav header; authenticated routes get the
 * sidebar shell. `/` is deliberately absent from this list: RootView serves
 * Today to a signed-in student, which belongs inside the shell.
 */
const GUEST_ROUTES = ["login", "register", "reset-password"];

/**
 * The marketing pages other than `/` (GitHub #325) carry their own nav and
 * footer, for a guest and a signed-in visitor alike: wrapping them in the
 * sidebar shell or the generic header would stack two navigations.
 */
const isLandingPage = computed(
  () => route.meta.variantSlug !== undefined && route.name !== "root",
);

const useShell = computed(
  () =>
    auth.isAuthenticated &&
    !GUEST_ROUTES.includes(route.name as string) &&
    !isLandingPage.value,
);

/** The landing brings its own marketing header; suppress the generic one there. */
const showAppHeader = computed(
  () => route.name !== "root" && !isLandingPage.value,
);

/** The guest landing carries its own complete footer. Avoid a second legal strip. */
const showGuestFooter = computed(
  () => route.name !== "root" && !isLandingPage.value,
);
</script>

<template>
  <template v-if="useShell">
    <AppShell>
      <RouterView />
    </AppShell>
  </template>
  <template v-else>
    <AppHeader v-if="showAppHeader" />
    <RouterView />
    <!-- PE-1.4 — guest routes need the legal strip unless the view owns it. -->
    <div v-if="showGuestFooter" class="guest-footer">
      <AppFooter />
    </div>
  </template>
  <!-- rl-lif9 — every route, every visitor, until they answer. -->
  <AnalyticsConsentBanner :beside-sidebar="useShell" />
</template>

<style scoped>
.guest-footer {
  padding: 0 var(--sp-8) var(--sp-6);
}
</style>
